import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, ExternalLink, Loader2, MapPin } from 'lucide-react';
import { authFetch } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import SalesHistoryList from './SalesHistoryList';

export default function DueDiligencePanel({ address, canRun, autoRun = false }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    if (!canRun) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch('/api/properties/due-diligence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Due diligence lookup failed');
      }
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoRun && canRun && address && !report && !loading && !error) {
      run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, canRun, address]);

  if (!report && !loading && !error) {
    return (
      <div className="bg-slate-900 border border-violet-500/30 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-violet-300 text-sm font-semibold flex items-center gap-2">
              <ClipboardCheck size={16} />
              No listing found for this address
            </p>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              Run property due diligence to analyse an off-market or pre-listing address using
              Land Registry sales, neighbourhood data, and EPC records.
            </p>
            <p className="text-slate-500 text-xs mt-2 font-mono">{address}</p>
          </div>
          <button
            type="button"
            onClick={run}
            disabled={!canRun}
            title={canRun ? 'Analyse this address' : 'Available on Basic or Pro plans'}
            className={`shrink-0 px-4 py-2.5 rounded-xl text-sm font-semibold inline-flex items-center gap-2 transition-colors ${
              canRun
                ? 'bg-violet-600 hover:bg-violet-500 text-white'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed opacity-60'
            }`}
          >
            <ClipboardCheck size={16} />
            Property due diligence
          </button>
        </div>
        {!canRun && (
          <p className="text-xs text-slate-500 mt-3">
            Property due diligence is available on Basic or Pro plans.{' '}
            <Link to="/choose-plan" className="text-emerald-400 hover:underline">View plans</Link>
          </p>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 py-10 bg-slate-900 border border-violet-500/30 rounded-2xl text-slate-400">
        <Loader2 size={20} className="animate-spin text-violet-400" />
        Running due diligence…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5 text-red-300 text-sm">
        {error}
      </div>
    );
  }

  const nb = report.neighbourhood || {};
  const sales = report.sales_history || [];

  return (
    <section className="bg-slate-900 border border-violet-500/30 rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-800 bg-violet-500/5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-violet-300 text-sm font-semibold">Property due diligence — off-market</p>
        {report.listed && report.property_id && (
          <Link
            to={`/properties/${report.property_id}`}
            className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
          >
            View listing <ExternalLink size={12} />
          </Link>
        )}
      </div>

      <div className="p-5 space-y-5">
        <div>
          <p className="text-white font-medium">{report.address}</p>
          <p className="text-slate-500 text-sm mt-1">{report.postcode}</p>
        </div>

        {report.epc && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="EPC rating" value={report.epc.energy_rating || report.epc.current_energy_rating || '—'} />
            <Stat label="Property type" value={report.epc.property_type || report.epc.mapped_type || '—'} />
            <Stat label="Floor area" value={report.epc.floor_area_sqm || report.epc.total_floor_area ? `${report.epc.floor_area_sqm || report.epc.total_floor_area} m²` : '—'} />
            <Stat label="Built form" value={report.epc.built_form || '—'} />
          </div>
        )}

        {nb.postcode && (
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/neighbourhood/${encodeURIComponent(nb.postcode || report.postcode)}`}
              className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
            >
              <MapPin size={14} />
              Open neighbourhood report
            </Link>
            {nb.imd_band && <span className="text-xs text-slate-500">IMD: {nb.imd_band}</span>}
            {nb.avg_price_1yr && (
              <span className="text-xs text-slate-500">
                Avg sale (1yr): {formatCurrency(nb.avg_price_1yr)}
              </span>
            )}
          </div>
        )}

        {sales.length > 0 && (
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Nearby Land Registry sales</p>
            <SalesHistoryList sales={sales} limit={6} />
          </div>
        )}

        {!report.epc && sales.length === 0 && !nb.postcode && (
          <p className="text-slate-500 text-sm">Limited records found for this address.</p>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-sm text-white font-medium mt-1 capitalize">{value}</p>
    </div>
  );
}
